import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './ThePitch.module.css';

export default function ThePitch() {
  const beats = useMemo(
    () => [
      {
        kicker: 'Everybody has contacts.',
        line: 'Almost nobody has relationship context—what matters, what changed, and what to do next.',
      },
      {
        kicker: 'Business cards are dead weight.',
        line: 'What you actually need is a living identity that updates once and stays current everywhere.',
      },
      {
        kicker: 'Most “networking” is leaky.',
        line: 'You meet someone, you vibe, then the moment evaporates because the follow-up never happens.',
      },
      {
        kicker: 'Sharing shouldn’t require a social feed.',
        line: 'Families, teams, cohorts, and communities need private-by-default sharing with real permissions.',
      },
      {
        kicker: 'Now imagine this:',
        line: 'Scan → view → connect → both people instantly save the relationship in their own system.',
      },
    ],
    []
  );

  const [step, setStep] = useState(0);
  const isDone = step >= beats.length;

  return (
    <div className={styles.container}>
      <Helmet>
        <title>The Pitch | MyPeopleDatabase</title>
        <meta
          name="description"
          content="A pep-rally style build-up to the MPD ID + private sharing platform: relationships with real context, shared safely, and kept current."
        />
      </Helmet>

      <div className={styles.content}>
        <h1 className={styles.title}>The Pitch</h1>

        <div className={styles.hero}>
          <p className={styles.heroLead}>Let’s build the anticipation—because this is obvious once you see it.</p>
          <p className={styles.heroSub}>
            You’re not looking at “another app.” You’re looking at the missing layer for human relationships.
          </p>
        </div>

        {!isDone ? (
          <div className={styles.card}>
            <div className={styles.cardKicker}>
              Build-up {step + 1} of {beats.length}
            </div>
            <div className={styles.cardQuestion}>
              <div>{beats[step].kicker}</div>
              <div style={{ marginTop: '0.75rem' }}>{beats[step].line}</div>
            </div>
            <div className={styles.actionsRow}>
              <button type="button" className={styles.primaryButton} onClick={() => setStep(s => s + 1)}>
                Keep going →
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setStep(beats.length)}
              >
                I’m ready
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.revealCard}>
              <div className={styles.revealTitle}>The moment it clicks</div>
              <p className={styles.revealLead}>
                MyPeopleDatabase is the private-by-default platform for relationship context—and MPD ID is the
                modern replacement for the business card.
              </p>

              <div className={styles.revealGrid}>
                <div className={styles.revealTile}>
                  <div className={styles.tileTitle}>MPD ID (link + QR)</div>
                  <div className={styles.tileBody}>One identity page. One scan. Always up to date.</div>
                </div>
                <div className={styles.revealTile}>
                  <div className={styles.tileTitle}>Context-first relationships</div>
                  <div className={styles.tileBody}>
                    Who they are. Why they matter. The next action. The timeline. The real story.
                  </div>
                </div>
                <div className={styles.revealTile}>
                  <div className={styles.tileTitle}>Private sharing, real permissions</div>
                  <div className={styles.tileBody}>
                    Invite-only groups for families, teams, cohorts—without turning into a social network.
                  </div>
                </div>
              </div>

              <div className={styles.talkTrack}>
                <div className={styles.talkTrackTitle}>Say it like a rally call</div>
                <ul className={styles.talkTrackList}>
                  <li className={styles.talkTrackItem}>
                    “We’re done losing opportunities because our follow-up system is a random pile of notes.”
                  </li>
                  <li className={styles.talkTrackItem}>
                    “We’re done trading dead business cards. One link. One QR. Always current.”
                  </li>
                  <li className={styles.talkTrackItem}>
                    “We’re done with public feeds. Sharing is private by default—with permissions.”
                  </li>
                  <li className={styles.talkTrackItem}>
                    “We keep the relationship context—so the next right action becomes automatic.”
                  </li>
                </ul>
              </div>

              <div className={styles.ctaRow}>
                <Link to="/mpd-id" className={styles.primaryCta}>
                  See MPD ID →
                </Link>
                <Link to="/q-and-a" className={styles.secondaryCta}>
                  Do the Q &amp; A →
                </Link>
                <button type="button" className={styles.ghostCta} onClick={() => setStep(0)}>
                  Run it back
                </button>
              </div>
            </div>

            <div className={styles.footerLinks}>
              <Link to="/buy" className={styles.linkButton}>
                Buy Now →
              </Link>
              <Link to="/vision" className={styles.linkButton}>
                See the Vision →
              </Link>
              <Link to="/business-plan" className={styles.linkButton}>
                Business Plan →
              </Link>
              <Link to="/contact-us" className={styles.linkButton}>
                Talk to us →
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
